'use strict';

const { BaseWidget } = require('./BaseWidget');
const { h } = require('./dom');
const { BX } = require('./theme');
const { MerchantSignupWidget } = require('./MerchantSignupWidget');
const { BucksBoxClient } = require('../BucksBoxClient');

const STEP_TITLES = [
  'Mobile Verification', 'OTP Verification', 'PAN Verification', 'Account Verification', 'Documents', 'Review & Submit',
];

// docId/requiresNumber/image-count for the document codes this widget
// specially handles (PAN/bank prefill, Aadhaar's server-enforced 2-image
// rule, store images' fixed docid) — ported from webpayment's
// app/utils/documentRegistry.ts, not that whole ~40-entry table:
// /onboarding/upi/isg/compliance/status already computes and returns every
// category/document's required/uploaded/compliant state itself (see
// isgComplianceStatus()), so this widget renders off THAT response, and only
// needs local metadata for behavior the server doesn't describe there (how
// many images a doc needs, whether it's a PAN/bank doc to pre-fill). Any doc
// code not listed here falls back to DEFAULT: one generic image, no number.
const DOC_META = {
  DEFAULT: { docId: 0, images: 1, requiresNumber: false },
  AADHAAR: { docId: 55, images: 2, requiresNumber: true, label: 'Aadhaar Number' }, // paymentSystem hard-validates >=2 images for exactly this code
  PARTNER1_AADHAAR: { docId: 55, images: 2, requiresNumber: true, label: 'Aadhaar Number', partner: 1 },
  PARTNER2_AADHAAR: { docId: 55, images: 2, requiresNumber: true, label: 'Aadhaar Number', partner: 2 },
  DRIVING_LICENSE: { docId: 5, images: 1, requiresNumber: true, label: 'Driving License Number' },
  VOTER_ID: { docId: 2, images: 1, requiresNumber: true, label: 'Voter ID Number' },
  PASSPORT: { docId: 1, images: 1, requiresNumber: true, label: 'Passport Number' },
  INDIVIDUAL_PAN: { docId: 63, images: 1, kind: 'pan' },
  PARTNER1_INDIVIDUAL_PAN: { docId: 63, images: 1, kind: 'pan', partner: 1 },
  PARTNER2_INDIVIDUAL_PAN: { docId: 63, images: 1, kind: 'pan', partner: 2 },
  BANK_PASSBOOK: { docId: 8, images: 1, kind: 'bank' },
  INDOOR_STORE_IMAGE: { docId: 999, images: 1 },
  OUTDOOR_STORE_IMAGE: { docId: 999, images: 1 },
};

function docMeta(code) { return DOC_META[code] || DOC_META.DEFAULT; }

function autoDocNumber(docType) {
  const now = new Date();
  return `${docType}_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Reads the claims out of a JWT's payload segment — no signature
 * verification, just parsing (paymentSystem already verified it server-side
 * before issuing it; this is purely a client-side read of what it already
 * told us). verify-otp's token payload carries `merchantId` directly
 * ({userId, merchantId, role}), which is otherwise not present anywhere
 * else in that endpoint's JSON response — letting this widget resolve its
 * own merchantId immediately after OTP, without waiting on a follow-up
 * profile fetch.
 */
function decodeJwtPayload(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = typeof atob !== 'undefined'
      ? decodeURIComponent(atob(padded).split('').map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join(''))
      : Buffer.from(padded, 'base64').toString('utf8');
    return JSON.parse(json);
  } catch (err) {
    return null;
  }
}

/**
 * MerchantOnboardingWidget — post-registration merchant KYC completion:
 * PAN verification -> bank account verification -> compliance document
 * upload -> review & submit. Ported directly onto the ISG-specific
 * onboarding endpoints paymentSystem's real merchant-facing KYC screen
 * uses (webpayment's /merchant/onboarding/isg, components/Merchant/
 * Onboarding/ISGOnboard.vue) — not the generic onboading/onboarding
 * endpoints, which are a different, mostly-unused code path in the
 * reference app. Authenticated the same way as every other module in this
 * SDK, with the merchant's *own* clientId/key (issued at their own
 * registration — see MerchantSignupWidget/RegistrationModule for that
 * earlier step).
 *
 * Deliberately has NO business-type step: ISGOnboard.vue treats business
 * type as already fixed from registration (read-only there) — it's
 * MerchantSignupWidget's job, not re-askable here anyway (paymentSystem has
 * no "change business type" endpoint in this flow). Also deliberately does
 * NOT re-ask legal_name/email/mobile/addresses — MerchantSignupWidget's
 * full registration form already captured all of that; this widget only
 * pulls identity fields back via getOwnMerchantForOnboarding() to satisfy
 * what the PAN/account verification and final submit calls need.
 *
 * PAN must be verified before bank account verification — paymentSystem's
 * own bank-verify status writer throws if the interface's MerchantServiceKyc
 * row doesn't exist yet, and only PAN verification creates it defensively;
 * this widget's fixed step order (PAN always before Account) avoids that.
 *
 * Final submit calls /merchant/kyc/submit (a pure mstatus->SUBMITTED status
 * flip), not paymentSystem's /onboarding/upi/isg/submit/onboading — that
 * route dereferences several merchant/address/PAN/bank fields with no
 * null-checks and fires a live call to the ISG partner API; webpayment's own
 * ISGOnboard.vue deliberately avoids it (its call is commented out in
 * source) in favor of the same safe endpoint mirrored here.
 *
 * const client = new BucksBoxClient({ environment: 'staging', clientId, key }); // the merchant's own credentials
 * const widget = new MerchantOnboardingWidget(client, { onSuccess, onError });
 * widget.mount('#bbx-onboarding').start();
 *
 * Starts with mobile number + OTP verification (mirrors payapp's
 * pages/index.vue login gate, which is what actually precedes the KYC
 * screen in production) — client.onboarding.sendOtp/verifyOtp, same
 * authenticated gateway pipeline as everything else here. This is a
 * confirmation gate, not a replacement for clientId/key: paymentSystem's
 * /send-otp and /verify-otp don't look at the caller's identity at all
 * (they resolve purely from phone+otp).
 *
 * If the verified mobile number has no merchant account yet — or this.client
 * turns out not to actually be scoped to that merchant (e.g. constructed
 * with a *vendor's* own clientId) — this hands off to MerchantSignupWidget
 * in place, pre-filling the verified number, and resumes onboarding
 * automatically once registration succeeds using a *newly minted*
 * BucksBoxClient built from the merchantId/clientId/key that registration
 * returns. That matters: every onboarding.* route here is merchant-scoped
 * on paymentSystem's side, so a vendor's own clientId/key could never call
 * them directly — only the fresh merchant-scoped client minted after
 * registration (or resolved via /register's existing-merchant branch) can.
 * If you already hold a specific merchant's own clientId/key (e.g. saved
 * server-side from an earlier MerchantSignupWidget run) and just want OTP as
 * a re-confirmation step, construct this widget with that merchant client
 * instead; the handoff branch is then not expected to trigger.
 *
 * Resumable: on every mount past OTP, this fetches the merchant's current
 * KYC status (kycStatus.{pan,bank,aadhaar,storeImage}) and jumps straight to
 * whichever step is still incomplete, matching ISGOnboard.vue's own
 * resumeFromKycStatus() — a merchant who already verified PAN in an earlier
 * session isn't asked to do it again.
 */
class MerchantOnboardingWidget extends BaseWidget {
  start() {
    this._p = {
      step: 1,
      panVerified: false,
      accountVerified: false,
    };
    this._complianceStatus = null;
    this._docView = { screen: 'categories', category: null, docType: null };
    this.screenMobile();
    return this;
  }

  // ── Step 1: Mobile Number ───────────────────────────────────────────

  screenMobile(errorMsg) {
    this.setStepHeader();
    const p = this._p;
    const mobileField = this.field({ label: 'Mobile Number', inputmode: 'numeric', maxlength: '10', placeholder: '10-digit mobile', value: p.mobile || '', onInput: (v) => { p.mobile = v.replace(/\D/g, '').slice(0, 10); } });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    this.showScreen(
      h('div', {}, [
        h('p', { class: 'bbx-hint', text: 'Enter your registered mobile number — a verification code will be sent to it.' }),
        mobileField.node,
        errorSlot,
        h('button', {
          class: 'bbx-btn', text: 'Send OTP',
          onClick: async (e) => {
            if (!/^\d{10}$/.test(p.mobile || '')) return errorSlot.replaceChildren(this.banner('error', 'Enter a valid 10-digit mobile number'));
            e.target.disabled = true;
            errorSlot.replaceChildren();
            this.renderLoading('Sending OTP…');
            try {
              const res = await this.client.onboarding.sendOtp({ phone: p.mobile });
              if (res?.statusCode !== '00') {
                this.fail(res, res?.message);
                e.target.disabled = false;
                this.screenMobile(res?.message || 'Could not send OTP');
                return;
              }
              this._p.step = 2;
              this.screenStep();
            } catch (err) {
              this.fail(err);
              e.target.disabled = false;
              this.screenMobile(this.errorMessage(err, 'Could not send OTP'));
            }
          },
        }),
      ]),
    );
  }

  // ── Step 2: OTP Verification ────────────────────────────────────────

  screenOtp(errorMsg) {
    this.setStepHeader();
    const p = this._p;
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    const otp = this.buildOtpInput({
      onComplete: async (value) => {
        try {
          const res = await this.client.onboarding.verifyOtp({ phone: p.mobile, otp: value });
          if (res?.statusCode !== '00') {
            otp.reset();
            errorSlot.replaceChildren(this.banner('error', res?.message || 'Incorrect OTP'));
            return;
          }
          const data = res.data || {};

          if (!data.token) {
            // OTP matched, but paymentSystem found no User row for this
            // mobile number (auth.routes.js#verify-otp returns
            // data.token:null in exactly this case) — there's no merchant
            // record yet to onboard. Mirrors payapp's pages/index.vue,
            // which routes this exact case to /register: hand off to
            // MerchantSignupWidget instead of dead-ending, then resume
            // onboarding once registration completes (see handoffToRegistration).
            otp.stop();
            this.handoffToRegistration();
            return;
          }

          // Resolve merchantId as early as possible — several onboarding.*
          // routes fall back to a merchantId in the request body when
          // server-side identity resolution doesn't populate it
          // (req.user?.merchantId || req.body.merchantId), so every call
          // below sends this explicitly rather than relying solely on that
          // resolution succeeding.
          const claims = decodeJwtPayload(data.token);
          if (claims?.merchantId) p.merchantId = claims.merchantId;

          if (data.role && data.role !== 'merchant') {
            // A User row exists for this mobile number, but it isn't a
            // merchant account (e.g. a vendor/aggregator testing their own
            // number) — nothing to onboard here.
            otp.stop();
            this.screenMerchantStatus({
              tone: 'error',
              title: 'Not a Merchant Account',
              message: 'This mobile number is verified, but it isn\'t registered as a merchant.',
            });
            return;
          }

          if (!data.merchant) {
            // Verified user exists with role "merchant", but has no
            // Merchant/MerchantInfo record yet (auth.routes.js#verify-otp
            // returns data.merchant:null whenever merchant.merchantinfo is
            // missing) — an incomplete/abandoned registration. Mirrors
            // payapp's pages/index.vue, which routes this exact case to
            // /register too: hand off to MerchantSignupWidget the same way
            // as the "no account at all" branch above.
            otp.stop();
            this.handoffToRegistration();
            return;
          }

          const mstatus = data.merchant.mstatus;
          const kycStatus = data.merchant.upiIsgKyc?.status;

          if (mstatus !== 'PENDING') {
            // mstatus (Merchant.mstatus — the business-approval lifecycle:
            // PENDING -> SUBMITTED -> APPROVED/ONBOARDED, or REJECTED) has
            // already moved past PENDING, i.e. KYC was already submitted at
            // some point — mirrors payapp's pages/index.vue "KYC Submitted.
            // Pending approval." branch. Re-entering the onboarding steps
            // at this point would be pointless (nothing left to submit), so
            // this is a terminal status screen, not a step to resume into.
            otp.stop();
            this.screenMerchantStatus({
              tone: 'warning',
              title: 'Merchant Under Review',
              message: `Your merchant KYC has already been submitted (status: ${mstatus}) and is under review — no further action is needed here right now.`,
            });
            return;
          }

          if (kycStatus && kycStatus !== 'PENDING') {
            // mstatus is still PENDING (nothing submitted yet), but the
            // ISG-specific service-KYC row already exists with some other
            // definitive status (e.g. VERIFIED/REJECTED from a prior
            // onboarding attempt) — not something to resume into via PAN
            // verification again.
            otp.stop();
            this.screenMerchantStatus({
              tone: 'warning',
              title: 'Merchant Under Review',
              message: `Your merchant KYC status is currently "${kycStatus}" — under review.`,
            });
            return;
          }

          // mstatus === 'PENDING' && (kycStatus === 'PENDING' OR no
          // UPI/ISG MerchantServiceKyc row exists at all yet — verify-otp
          // scopes that lookup to service:"UPI", interface:"ISG", so a
          // merchant who only has e.g. an AEPS service-KYC row, or none at
          // all, also lands here with kycStatus undefined). This widget IS
          // the ISG onboarding flow — unlike payapp's pages/index.vue,
          // which has a dashboard to fall back to and only routes into
          // /mobile/isg/onboard for the exact 'PENDING' case, this widget
          // has nowhere else to send a merchant who simply hasn't started
          // yet, so "never started" is treated the same as "PENDING":
          // isgVerifyPan/createServiceKyc create that row on first use.
          p.primary_mobile = p.mobile;
          this.screenInit();
        } catch (err) {
          otp.reset();
          errorSlot.replaceChildren(this.banner('error', this.errorMessage(err, 'Incorrect OTP')));
        }
      },
      onResend: () => this.client.onboarding.sendOtp({ phone: p.mobile }),
    });

    this.showScreen(h('div', {}, [h('p', { class: 'bbx-hint', text: `Code sent to +91 ${p.mobile}` }), otp.node, errorSlot]));
  }

  /**
   * One-time setup + resume logic, run once after OTP confirms an existing
   * account: validates this.client is actually merchant-scoped, pulls the
   * richer ISG onboarding profile (id/pan/business_type), best-effort
   * initializes the ISG service-KYC row + OTP-confirmed flag (mirrors
   * ISGOnboard.vue's onMounted exactly — both calls there are fire-and-
   * forget too), loads compliance status, then jumps to whichever step is
   * still incomplete.
   */
  async screenInit() {
    this.setHeader({ title: 'Complete Onboarding', subtitle: 'Setting up onboarding…', showBack: false });
    this.renderLoading('Setting up onboarding…');

    try {
      // merchant/me is checkRole("merchant")-gated and gives a clean 403 on
      // a role mismatch — used purely as a scope check here.
      // getOwnMerchantForOnboarding() below has no such gate (it resolves
      // merchant purely from the caller's own decoded identity and 500s
      // opaquely for a wrong-scoped caller), so this is the one reliable
      // signal that this.client isn't actually scoped to the merchant OTP
      // just verified (e.g. constructed with a *vendor's* own clientId).
      //await this.client.onboarding.getProfile();
    } catch (err) {
      if (err?.status === 403) {
        // verify-otp's "account exists" response only carries a JWT, which
        // this SDK's apiKey/AES pipeline can't use — there's no way to mint
        // a merchant-scoped BucksBoxClient from it directly. The one path
        // that *does* return that merchant's own apiKey/SaltAESKey is
        // /register's existing-merchant branch (screenResult() reads
        // res.data.user.apiKey/SaltAESKey for both new and existing
        // accounts) — so recover exactly like the "no account yet" case.
        this.handoffToRegistration();
        return;
      }
      // Any other failure: fall through and still try the richer profile below.
    }

    try {
      const res = await this.client.onboarding.getOwnMerchantForOnboarding();
      const profile = res?.data || {};
      this._p.merchantId = profile.id || this._p.merchantId;
      this._p.legal_name = profile.merchant_name || this._p.legal_name || '';
      this._p.primary_email_id = profile.primary_email_id || this._p.primary_email_id || '';
      this._p.primary_mobile = profile.primary_mobile || this._p.primary_mobile || '';
      // pan_dob/pan_father_name are never persisted by paymentSystem's PAN
      // upsert (only pan/pan_name are) — left blank even when pan is already verified.
      if (profile.pan) this._p.pan = { pan: profile.pan, name: profile.pan_name || '', dobRaw: '', fathername: '' };
    } catch (err) {
      // Supplementary profile data — the PAN/account verification steps ahead still work without it.
    }

    if (this._p.merchantId) {
      try { await this.client.onboarding.createServiceKyc({ merchantId: this._p.merchantId, service: 'UPI', interfaceName: 'ISG' }); } catch (err) { /* best-effort, matches ISGOnboard.vue's own catch-and-continue */ }
      try { await this.client.onboarding.setVerifyOtp({ merchantId: this._p.merchantId }); } catch (err) { /* best-effort — paymentSystem always replies '00' here anyway */ }
    }

    await this.refreshComplianceStatus();
    this.resumeFromKycStatus();
    this.screenStep();
  }

  async refreshComplianceStatus() {
    try {
      const res = await this.client.onboarding.isgComplianceStatus({ merchantId: this._p.merchantId });
      this._complianceStatus = res?.statusCode === '00' ? res : null;
    } catch (err) {
      this._complianceStatus = null;
    }
  }

  /** Jumps to the first still-incomplete step, mirroring ISGOnboard.vue's resumeFromKycStatus(). */
  resumeFromKycStatus() {
    const kyc = this._complianceStatus?.kycStatus;
    if (!kyc || !kyc.pan) { this._p.step = 3; return; }
    this._p.panVerified = true;
    if (!kyc.bank) { this._p.step = 4; return; }
    this._p.accountVerified = true;
    if (!kyc.aadhaar || !kyc.storeImage) { this._p.step = 5; return; }
    this._p.step = 6;
  }

  /**
   * Terminal, non-numbered status screen for every post-OTP case where
   * this widget's own step flow doesn't apply — not a merchant account, or
   * a merchant whose KYC is already submitted/under review/in some other
   * non-onboardable state (mirrors payapp's pages/index.vue "You are not a
   * merchant" / "KYC Submitted. Pending approval." / "Verified!" branches,
   * which route to other pages this SDK widget has no equivalent of).
   * Reports the outcome via onSuccess so the host page can decide what to
   * do next (e.g. show its own dashboard), the same as screenResult().
   */
  screenMerchantStatus({ tone, title, message }) {
    this.setHeader({ title: 'Complete Onboarding', showBack: false });
    const iconBg = tone === 'error' ? BX.error2 : BX.warning;

    this.showScreen(
      h('div', {}, [
        h('div', { class: 'bbx-status-icon', style: { background: iconBg }, text: tone === 'error' ? '!' : '⏳' }),
        h('p', { class: 'bbx-status-title', text: title }),
        h('p', { class: 'bbx-status-sub', text: message }),
        h('button', {
          class: 'bbx-btn', text: 'Done',
          onClick: () => {
            this.succeed({ status: 'not_onboardable', title, message });
            this.opts.onClose && this.opts.onClose();
          },
        }),
      ]),
    );
  }

  /**
   * No merchant account exists for the verified mobile number (or this
   * widget's client turned out not to be merchant-scoped) — mirrors
   * payapp's pages/index.vue routing that exact case to /register. Mounts
   * MerchantSignupWidget in place of this widget's own card (same target
   * element — a clean handoff, not a nested widget), pre-filling/locking
   * the already-verified mobile number. On success it mints a *fresh*
   * BucksBoxClient from the returned merchantId/clientId/key and resumes
   * onboarding with that — see _resumeAfterSignup for why a new client is
   * required rather than reusing this.client.
   */
  handoffToRegistration() {
    this.unmount();
    const signup = new MerchantSignupWidget(this.client, {
      mobileNumber: this._p.mobile,
      onError: (err) => { this.opts.onError && this.opts.onError(err); },
      onSuccess: (result) => this._resumeAfterSignup(result),
      onClose: () => {}, // signup's Done button fires onSuccess+onClose together; onSuccess already drives the handoff, so this is intentionally a no-op rather than bubbling up as if the whole onboarding flow closed
    });
    this._activeSignup = signup;
    signup.mount(this.target).start();
  }

  /** Tears down the embedded signup widget and re-mounts this one, now
   *  authenticated as the newly-registered merchant, to continue onboarding. */
  _resumeAfterSignup(result) {
    if (this._activeSignup) { this._activeSignup.unmount(); this._activeSignup = null; }

    this.client = new BucksBoxClient({
      gatewayUrl: this.client._http.gatewayUrl,
      clientId: result.clientId,
      key: result.key,
    });

    this._p.merchantId = result.merchantId || this._p.merchantId;
    this._p.primary_mobile = this._p.mobile;
    this.mount(this.target);
    this.screenInit();
  }

  // ── Shared chrome ───────────────────────────────────────────────────

  stepSubtitle() {
    return `Step ${this._p.step} of 6 · ${STEP_TITLES[this._p.step - 1]}`;
  }

  screenStep(errorMsg) {
    const renderers = [
      () => this.screenMobile(errorMsg),
      () => this.screenOtp(errorMsg),
      () => this.screenPanVerification(errorMsg),
      () => this.screenAccountVerification(errorMsg),
      () => this.screen5Documents(),
      () => this.screenReview(errorMsg),
    ];
    renderers[this._p.step - 1]();
  }

  setStepHeader() {
    this.setHeader({
      title: 'Complete Onboarding',
      subtitle: this.stepSubtitle(),
      showBack: this._p.step > 1,
      onBack: () => { this._p.step--; this.screenStep(); },
    });
  }

  selectField({ label, options, value, placeholder, onChange }) {
    const select = h(
      'select',
      { class: 'bbx-input', onChange: (e) => onChange && onChange(e.target.value) },
      [placeholder ? h('option', { value: '', text: placeholder }) : null, ...options.map((o) => h('option', { value: String(o.value), text: o.label }))],
    );
    if (value != null && value !== '') select.value = String(value);
    return { node: h('div', { class: 'bbx-field' }, [label ? h('label', { text: label }) : null, select]), select };
  }

  // ── Step 3: PAN Verification ────────────────────────────────────────
  // ISGOnboard.vue's own equivalent (handleStep3Pan) — the real, actually-
  // called PAN verification for this flow (the generic onboading.* verifyPan
  // this SDK also exposes is a different, mostly-unused code path).

  screenPanVerification(errorMsg) {
    this.setStepHeader();
    const p = this._p;
    if (!p.pan) p.pan = { pan: '', name: p.legal_name || '', dobRaw: '', fathername: '' };
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    if (p.panVerified) {
      this.showScreen(
        h('div', {}, [
          h('div', { class: 'bbx-card' }, [
            h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'PAN' }), h('span', { class: 'bbx-row-v bbx-mono', text: p.pan.pan || '—' })]),
            h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Name' }), h('span', { class: 'bbx-row-v', text: p.pan.verifiedName || p.pan.name || '—' })]),
          ]),
          this.banner('success', 'PAN already verified.'),
          h('button', { class: 'bbx-btn', text: 'Continue', onClick: () => { this._p.step = 4; this.screenStep(); } }),
        ]),
      );
      return;
    }

    const panField = this.field({ label: 'PAN Number', placeholder: 'e.g. ABCDE1234F', maxlength: '10', value: p.pan.pan, onInput: (v) => { p.pan.pan = v.toUpperCase(); } });
    const nameField = this.field({ label: 'Full Name (as on PAN)', value: p.pan.name, onInput: (v) => { p.pan.name = v; } });
    const dobField = this.field({ label: 'Date of Birth', type: 'date', value: p.pan.dobRaw, onInput: (v) => { p.pan.dobRaw = v; } });
    const fatherField = this.field({ label: "Father's Name", value: p.pan.fathername, onInput: (v) => { p.pan.fathername = v; } });

    this.showScreen(
      h('div', {}, [
        panField.node, nameField.node, dobField.node, fatherField.node,
        errorSlot,
        h('button', {
          class: 'bbx-btn', text: 'Verify PAN',
          onClick: async (e) => {
            const pan = p.pan.pan.toUpperCase();
            if (pan.length !== 10) return errorSlot.replaceChildren(this.banner('error', 'Enter a valid 10-character PAN number'));
            if (!p.pan.name.trim()) return errorSlot.replaceChildren(this.banner('error', 'Enter the name exactly as on the PAN card'));
            if (!p.pan.dobRaw) return errorSlot.replaceChildren(this.banner('error', 'Enter the date of birth'));
            if (!p.pan.fathername.trim()) return errorSlot.replaceChildren(this.banner('error', "Enter the father's name"));

            const [y, m, d] = p.pan.dobRaw.split('-');
            const dob = `${d}/${m}/${y}`;

            e.target.disabled = true;
            errorSlot.replaceChildren();
            this.renderLoading('Verifying PAN…');
            try {
              const res = await this.client.onboarding.isgVerifyPan({ pan, name: p.pan.name.trim(), dob, fathername: p.pan.fathername.trim(), merchantId: p.merchantId });
              if (res?.statusCode !== '00') {
                this.fail(res, res?.message);
                e.target.disabled = false;
                this.screenPanVerification(res?.message || 'PAN verification failed');
                return;
              }
              p.pan.pan = pan;
              p.pan.verifiedName = res?.data?.pan_name || p.pan.name.trim();
              p.panVerified = true;
              await this.refreshComplianceStatus();
              this._p.step = 4;
              this.screenStep();
            } catch (err) {
              this.fail(err);
              e.target.disabled = false;
              this.screenPanVerification(this.errorMessage(err, 'PAN verification failed'));
            }
          },
        }),
      ]),
    );
  }

  // ── Step 4: Account (Bank) Verification ─────────────────────────────

  screenAccountVerification(errorMsg) {
    this.setStepHeader();
    const p = this._p;
    if (!p.account) p.account = { accountNumber: '', accountHolderName: p.legal_name || '', ifsc: '' };
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    if (p.accountVerified) {
      const summary = p.accountSummary;
      this.showScreen(
        h('div', {}, [
          h('div', { class: 'bbx-card' }, [
            h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Account Holder' }), h('span', { class: 'bbx-row-v', text: summary?.account_holder_name || p.account.accountHolderName || '—' })]),
            h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Account Number' }), h('span', { class: 'bbx-row-v bbx-mono', text: p.account.accountNumber || '—' })]),
            summary?.bank_name ? h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Bank' }), h('span', { class: 'bbx-row-v', text: summary.bank_name })]) : null,
          ]),
          this.banner('success', 'Bank account already verified.'),
          h('button', { class: 'bbx-btn', text: 'Continue', onClick: () => { this._p.step = 5; this._docView = { screen: 'categories', category: null, docType: null }; this.screenStep(); } }),
        ]),
      );
      return;
    }

    const accField = this.field({ label: 'Account Number', inputmode: 'numeric', value: p.account.accountNumber, onInput: (v) => { p.account.accountNumber = v.replace(/\D/g, ''); } });
    const holderField = this.field({ label: 'Account Holder Name', value: p.account.accountHolderName, onInput: (v) => { p.account.accountHolderName = v; } });
    const ifscField = this.field({ label: 'IFSC Code', value: p.account.ifsc, onInput: (v) => { p.account.ifsc = v.toUpperCase(); } });

    this.showScreen(
      h('div', {}, [
        accField.node, holderField.node, ifscField.node,
        errorSlot,
        h('button', {
          class: 'bbx-btn', text: 'Verify Account',
          onClick: async (e) => {
            if (!p.account.accountNumber) return errorSlot.replaceChildren(this.banner('error', 'Enter the account number'));
            if (!p.account.accountHolderName.trim()) return errorSlot.replaceChildren(this.banner('error', 'Enter the account holder name'));
            if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(p.account.ifsc)) return errorSlot.replaceChildren(this.banner('error', 'Enter a valid IFSC code'));

            e.target.disabled = true;
            errorSlot.replaceChildren();
            this.renderLoading('Verifying account…');
            try {
              const res = await this.client.onboarding.isgVerifyAccount({
                accountHolderName: p.account.accountHolderName.trim(),
                accountNumber: p.account.accountNumber,
                ifsc: p.account.ifsc,
                consent: 'Y',
                additionalData: 'true',
                merchantId: p.merchantId,
              });
              if (res?.statusCode !== '00') {
                this.fail(res, res?.message);
                e.target.disabled = false;
                // one ISG failure branch returns raw upstream status_desc instead of message
                this.screenAccountVerification(res?.message || res?.status_desc || 'Account verification failed');
                return;
              }
              p.accountVerified = true;
              p.accountSummary = res?.data || null;
              await this.refreshComplianceStatus();
              this._p.step = 5;
              this._docView = { screen: 'categories', category: null, docType: null };
              this.screenStep();
            } catch (err) {
              // paymentSystem can 500 here even after the account was
              // genuinely verified and persisted (see isgVerifyAccount's
              // JSDoc) — re-check compliance status before assuming failure.
              await this.refreshComplianceStatus();
              if (this._complianceStatus?.kycStatus?.bank) {
                p.accountVerified = true;
                this._p.step = 5;
                this._docView = { screen: 'categories', category: null, docType: null };
                this.screenStep();
                return;
              }
              this.fail(err);
              e.target.disabled = false;
              this.screenAccountVerification(this.errorMessage(err, 'Account verification failed'));
            }
          },
        }),
      ]),
    );
  }

  // ── Step 5: Documents (categories -> documents -> upload form) ─────
  // Sourced entirely from isgComplianceStatus()'s categories[] — every
  // required/uploaded/compliant count is already computed server-side from
  // the business-type document matrix; never recomputed client-side.

  isComplianceComplete() {
    const categories = this._complianceStatus?.categories || [];
    if (!categories.length) return false;
    return categories.every((cat) => cat.compliant);
  }

  screen5Documents() {
    this.setStepHeader();
    const view = this._docView;
    if (view.screen === 'categories') return this.screen5aCategories();
    if (view.screen === 'documents') return this.screen5bDocuments();
    return this.screen5cUploadForm();
  }

  screen5aCategories() {
    const categories = this._complianceStatus?.categories || [];
    const list = categories.length
      ? categories.map((cat) =>
          h('div', { class: `bbx-select-item${cat.compliant ? ' bbx-selected' : ''}`, style: { textAlign: 'left', marginBottom: '8px' }, onClick: () => { this._docView = { screen: 'documents', category: cat, docType: null }; this.screenStep(); } }, [
            h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } }, [
              h('div', { style: { fontWeight: '800', fontSize: '13px' }, text: cat.category.replace(/_/g, ' ') }),
              h('span', { class: 'bbx-hint', text: `${cat.uploaded} / ${cat.required}` }),
            ]),
          ]),
        )
      : [h('p', { class: 'bbx-hint', text: 'No document categories configured for this business type.' })];

    const complete = this.isComplianceComplete();

    this.showScreen(
      h('div', {}, [
        !complete ? this.banner('warning', 'Complete all required document categories before continuing.') : this.banner('success', 'All required documents uploaded.'),
        h('div', {}, list),
        h('button', {
          class: 'bbx-btn', text: complete ? 'Continue to Review' : 'Documents incomplete',
          onClick: () => { if (!complete) return; this._p.step = 6; this.screenStep(); },
        }),
      ]),
    );
  }

  screen5bDocuments() {
    const cat = this._docView.category;
    this.setHeader({ title: cat.category.replace(/_/g, ' '), showBack: true, onBack: () => { this._docView = { screen: 'categories', category: null, docType: null }; this.screenStep(); } });

    const list = (cat.documents || []).map((doc) =>
      h('div', { class: `bbx-select-item${doc.uploaded ? ' bbx-selected' : ''}`, style: { textAlign: 'left', marginBottom: '8px' }, onClick: () => { this._docView = { screen: 'form', category: cat, docType: doc.code }; this.screenStep(); } }, [
        h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } }, [
          h('div', { style: { fontWeight: '700', fontSize: '13px' }, text: doc.name || doc.code.replace(/_/g, ' ') }),
          h('span', { class: 'bbx-hint', text: doc.uploaded ? 'Uploaded' : (doc.mandatory ? 'Mandatory' : 'Optional') }),
        ]),
      ]),
    );

    this.showScreen(h('div', {}, list.length ? list : [h('p', { class: 'bbx-hint', text: 'No documents in this category.' })]));
  }

  screen5cUploadForm(errorMsg) {
    const { category: cat, docType } = this._docView;
    const meta = docMeta(docType);
    this.setHeader({ title: `Upload ${docType.replace(/_/g, ' ')}`, showBack: true, onBack: () => { this._docView = { screen: 'documents', category: cat, docType: null }; this.screenStep(); } });

    if (!this._docForms) this._docForms = {};
    if (!this._docForms[docType]) {
      // The merchant's own PAN/bank docs (not a partner's) pre-fill from
      // screenPanVerification/screenAccountVerification's already-verified
      // details, same principle as everywhere else in this widget: never
      // type the same thing twice.
      const p = this._p;
      this._docForms[docType] = meta.kind === 'pan'
        ? {
            pan: !meta.partner && p.pan
              ? { pan: p.pan.pan, pan_name: p.pan.verifiedName || p.pan.name }
              : { pan: '', pan_name: '' },
            docNumber: '', files: [],
          }
        : meta.kind === 'bank'
          ? {
              settlement: p.account
                ? { accountHolderName: p.accountSummary?.account_holder_name || p.account.accountHolderName, accountNumber: p.account.accountNumber, ifsc: p.account.ifsc, bankName: p.accountSummary?.bank_name || '' }
                : { accountHolderName: '', accountNumber: '', ifsc: '', bankName: '' },
              docNumber: '', files: [],
            }
          : { docNumber: '', files: [] };
    }
    const state = this._docForms[docType];

    const fields = [];
    if (meta.kind === 'pan') {
      fields.push(
        this.field({ label: 'PAN Number', value: state.pan.pan, onInput: (v) => { state.pan.pan = v.toUpperCase(); } }).node,
        this.field({ label: 'PAN Holder Name', value: state.pan.pan_name, onInput: (v) => { state.pan.pan_name = v; } }).node,
      );
    } else if (meta.kind === 'bank') {
      fields.push(
        this.field({ label: 'Account Holder Name', value: state.settlement.accountHolderName, onInput: (v) => { state.settlement.accountHolderName = v; } }).node,
        this.field({ label: 'Account Number', inputmode: 'numeric', value: state.settlement.accountNumber, onInput: (v) => { state.settlement.accountNumber = v.replace(/\D/g, ''); } }).node,
        this.field({ label: 'Bank Name', value: state.settlement.bankName, onInput: (v) => { state.settlement.bankName = v; } }).node,
      );
    } else if (meta.requiresNumber) {
      fields.push(this.field({ label: meta.label || 'Document Number', value: state.docNumber, onInput: (v) => { state.docNumber = v; } }).node);
    }

    const fileInput = h('input', {
      type: 'file', accept: 'image/*,.pdf', multiple: meta.images > 1,
      onChange: (e) => { state.files = Array.from(e.target.files || []); fileHint.textContent = `${state.files.length} / ${meta.images} file(s) selected`; },
    });
    const fileHint = h('p', { class: 'bbx-hint', text: `${state.files.length} / ${meta.images} file(s) selected` });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    this.showScreen(
      h('div', {}, [
        ...fields,
        h('div', { class: 'bbx-field' }, [h('label', { text: `Upload ${meta.images} image(s)/PDF` }), fileInput, fileHint]),
        errorSlot,
        h('button', {
          class: 'bbx-btn', text: 'Save Document',
          onClick: async (e) => {
            if (state.files.length !== meta.images) return errorSlot.replaceChildren(this.banner('error', `Please select exactly ${meta.images} file(s)`));
            if (meta.kind === 'pan' && !state.pan.pan) return errorSlot.replaceChildren(this.banner('error', 'Enter the PAN number'));
            if (meta.kind === 'bank' && !state.settlement.accountNumber) return errorSlot.replaceChildren(this.banner('error', 'Enter the account number'));
            if (meta.requiresNumber && !state.docNumber) return errorSlot.replaceChildren(this.banner('error', `Enter the ${(meta.label || 'document number').toLowerCase()}`));

            e.target.disabled = true;
            errorSlot.replaceChildren();
            this.showOverlay('Uploading document…');
            try {
              const imageIds = [];
              for (const file of state.files) {
                const base64 = await fileToBase64(file);
                // docid must match the codes paymentSystem hard-codes for its
                // auto-status logic (55 = Aadhaar, 999 = store image) — see DOC_META.
                const res = await this.client.onboarding.uploadComplianceImage({ file: base64, filename: `${docType}_${file.name}`, mimetype: file.type, docid: String(meta.docId), merchantId: this._p.merchantId });
                if (res?.statusCode !== '00') throw new Error(res?.message || 'Image upload failed');
                imageIds.push(res.data.id);
              }

              const docNumber = meta.kind === 'pan' ? state.pan.pan : meta.kind === 'bank' ? state.settlement.accountNumber : meta.requiresNumber ? state.docNumber : autoDocNumber(docType);
              const uploadRes = await this.client.onboarding.isgDocUpload({ doc_type: docType, doc_number: docNumber, images: imageIds, merchantId: this._p.merchantId });
              this.hideOverlay();
              if (uploadRes?.statusCode !== '00') {
                e.target.disabled = false;
                errorSlot.replaceChildren(this.banner('error', uploadRes?.message || 'Could not save document'));
                return;
              }
              await this.refreshComplianceStatus();
              // _docView.category is a snapshot from before the refresh — re-resolve
              // it from the fresh categories list so the document list shows the
              // just-uploaded doc as uploaded instead of stale "Pending".
              const freshCat = (this._complianceStatus?.categories || []).find((c) => c.category === cat.category) || cat;
              this._docView = { screen: 'documents', category: freshCat, docType: null };
              this.screenStep();
            } catch (err) {
              this.hideOverlay();
              this.fail(err);
              e.target.disabled = false;
              errorSlot.replaceChildren(this.banner('error', this.errorMessage(err, 'Could not upload document')));
            }
          },
        }),
      ]),
    );
  }

  // ── Step 6: Review & Submit ─────────────────────────────────────────

  screenReview(errorMsg) {
    this.setStepHeader();
    const p = this._p;
    const compliant = this.isComplianceComplete();
    const allDone = p.panVerified && p.accountVerified && compliant;
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    this.showScreen(
      h('div', {}, [
        h('div', { class: 'bbx-card' }, [
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'PAN Verification' }), h('span', { class: 'bbx-row-v', text: p.panVerified ? 'Verified' : 'Pending' })]),
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Bank Account' }), h('span', { class: 'bbx-row-v', text: p.accountVerified ? 'Verified' : 'Pending' })]),
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Documents' }), h('span', { class: 'bbx-row-v', text: compliant ? 'Complete' : 'Pending' })]),
        ]),
        allDone ? this.banner('success', 'All verifications complete — you can submit now.') : this.banner('warning', 'Complete every step above before submitting.'),
        errorSlot,
        h('button', {
          class: 'bbx-btn', text: 'Submit',
          onClick: async (e) => {
            if (!allDone) return errorSlot.replaceChildren(this.banner('error', 'Please complete all verification steps first'));
            e.target.disabled = true;
            errorSlot.replaceChildren();
            this.showOverlay('Submitting KYC…');
            try {
              const res = await this.client.onboarding.markKycSubmitted({ merchantId: this._p.merchantId });
              this.hideOverlay();
              if (res?.statusCode === '00') {
                this.screenResult(res);
              } else {
                this.fail(res, res?.message);
                e.target.disabled = false;
                this.screenReview(res?.message || 'Submission failed');
              }
            } catch (err) {
              this.hideOverlay();
              this.fail(err);
              e.target.disabled = false;
              this.screenReview(this.errorMessage(err, 'Submission failed'));
            }
          },
        }),
      ]),
    );
  }

  screenResult(res) {
    this.setHeader({ title: 'Complete Onboarding', showBack: false });
    const payload = { statusCode: res?.statusCode, message: res?.message, merchantId: res?.data?.id, mid: res?.data?.mid, status: res?.data?.status };

    this.showScreen(
      h('div', {}, [
        h('div', { class: 'bbx-status-icon', style: { background: BX.success2 }, text: '✓' }),
        h('p', { class: 'bbx-status-title', text: 'KYC Submitted' }),
        h('p', { class: 'bbx-status-sub', text: res?.message || 'Your KYC has been submitted for verification.' }),
        payload.mid ? h('div', { class: 'bbx-card' }, [h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Merchant ID' }), h('span', { class: 'bbx-row-v bbx-mono', text: String(payload.mid) })])]) : null,
        h('button', { class: 'bbx-btn', text: 'Done', onClick: () => { this.succeed(payload); this.opts.onClose && this.opts.onClose(); } }),
      ]),
    );
  }
}

module.exports = { MerchantOnboardingWidget };
